import {
  CompletionTargetUnit,
  goalInboxID,
  goalOtherID,
  Habit,
  HabitSchedule,
  IconFormNames,
  ID,
  lifeAreaOtherID,
  NewHabit,
  NewTask,
  PlannedOccurrence,
  Reminder,
  ReminderBasedOn,
  ScheduleEntry,
  ScheduleEntryState,
  Task,
  TaskAction,
  TaskActionType,
  TaskPriorityOptions,
  TaskType,
  Timestamp,
} from '@griply/types';
import {
  coerceScheduleRrule,
  isValidRrule,
  resolveTaskTypeForSchedules,
} from '@griply/utils';
import { endOfDay, startOfDay } from 'date-fns';

import { FirestoreDataConverter } from '../types/firestore';
import {
  firebaseObjectTimestampToDate,
  firebaseTimestampToDate,
} from './firebase-timestamp-to-date';
import { isValidDate } from './is-valid-date';

const knownActionTypes = Object.values(TaskActionType);

// Forward-compat: a future client may write an action type this version does not
// know. Drop the unknown action so the task renders as a plain task everywhere
// rather than reaching an unhandled render branch.
const coerceAction = (value: unknown): TaskAction | null => {
  if (!value || typeof value !== 'object') {
    return null;
  }
  const { type, meta } = value as { type?: unknown; meta?: { id?: unknown } };
  if (!knownActionTypes.includes(type as TaskActionType)) {
    return null;
  }
  return typeof meta?.id === 'string' ? (value as TaskAction) : null;
};

const coercePriority = (value: unknown): TaskPriorityOptions | null => {
  if (!value) {
    return null;
  }
  if (typeof value !== 'string') {
    return null;
  }
  if (
    !Object.values(TaskPriorityOptions).includes(value as TaskPriorityOptions)
  ) {
    return null;
  }
  return value as TaskPriorityOptions;
};

const coerceTaskType = (value: unknown): TaskType => {
  if (!Object.values(TaskType).includes(value as TaskType)) {
    return TaskType.Task;
  }
  return value as TaskType;
};

const coerceIconName = (value: unknown): IconFormNames | null => {
  if (!value) {
    return null;
  }
  if (typeof value !== 'string') {
    return null;
  }
  if (!Object.values(IconFormNames).includes(value as IconFormNames)) {
    return null;
  }
  return value as IconFormNames;
};

const isKnownEntryState = (value: unknown): value is ScheduleEntryState =>
  Object.values(ScheduleEntryState).includes(value as ScheduleEntryState);

const coerceCompletionTargetUnit = (value: unknown): CompletionTargetUnit =>
  Object.values(CompletionTargetUnit).includes(value as CompletionTargetUnit)
    ? (value as CompletionTargetUnit)
    : CompletionTargetUnit.Day;

// A future client may write an unknown `basedOn` or malformed entries; an absent field must stay null so
// the legacy `reminderTime` can still surface, while an empty array means "explicitly no reminders".
const coerceReminders = (value: unknown): Reminder[] | null => {
  if (!Array.isArray(value)) {
    return null;
  }
  return value.reduce<Reminder[]>((reminders, entry) => {
    if (!entry || typeof entry !== 'object') {
      return reminders;
    }
    const { id, offset, basedOn } = entry as Record<string, unknown>;
    if (typeof offset !== 'number' || !Number.isFinite(offset)) {
      return reminders;
    }
    reminders.push({
      id: typeof id === 'string' ? id : null,
      offset,
      basedOn: Object.values(ReminderBasedOn).includes(
        basedOn as ReminderBasedOn,
      )
        ? (basedOn as ReminderBasedOn)
        : ReminderBasedOn.Start,
    });
    return reminders;
  }, []);
};

export type TaskConverterOnError = (
  error: Error,
  context: { taskId: ID; rrule: string },
) => void;

export type TaskConverterOptions = {
  onError?: TaskConverterOnError;
};

// Sync set-before-use by the factory wrapper; safe because converters never interleave.
let activeOnError: TaskConverterOnError | undefined;
let activeReportedInvalidRrules = new Set<string>();

const baseConverter: FirestoreDataConverter<Task> = {
  fromFirestore: (snapshot) => {
    const rawData = firebaseObjectTimestampToDate(snapshot.data());
    const data = rawData as Record<string, unknown>;
    data.id = snapshot.id;
    const knownType = coerceTaskType(data.type);
    data.type = knownType;

    let parsedData: Partial<Task> = {
      type: TaskType.Task,
      createdAt: (data.createdAt as Date | undefined) ?? new Date(),
      updatedAt: (data.updatedAt as Date | undefined) ?? new Date(),
      startDate:
        ((data.startDate as Date | undefined)
          ? (data.startDate as Date)
          : (data.createdAt as Date | undefined)) ?? new Date(),
      reminderTime: (data.reminderTime as number | null | undefined) ?? null,
      reminders: coerceReminders(data.reminders),
      iconName: coerceIconName(data.iconName),
      parentIds: (data.parentIds as ID[])?.length
        ? (data.parentIds as ID[])
        : null,
      priority: coercePriority(data.priority),
      action: coerceAction(data.action),
      schedules: null,
    };

    if (
      [TaskType.Habit, TaskType.Repeating].includes(knownType) &&
      (data.schedules as HabitSchedule[] | undefined)?.length
    ) {
      const taskCreatedAt = parsedData.createdAt ?? new Date();

      parsedData = parsedData as unknown as Habit;
      parsedData.type = knownType as TaskType.Habit;
      // Habits and repeating tasks must never carry a `deadlineDeadline` —
      // their schedule defines due-ness. Stale values can survive a
      // Todo → Habit conversion and misclassify the task as overdue.
      parsedData.deadlineDeadline = null;
      parsedData.iconName =
        coerceIconName(data.iconName) ?? IconFormNames.Repeat;
      parsedData.schedules = (
        data.schedules as HabitSchedule[]
      ).map<HabitSchedule>((schedule) => {
        const validEntries = (schedule.entries ?? []).filter(
          (entry) => isValidDate(entry.date) && isKnownEntryState(entry.state),
        );
        const validCompletions = (schedule.completions ?? []).filter(
          (date): date is Timestamp => isValidDate(date),
        );
        const validSkips = (schedule.skips ?? []).filter(
          (date): date is Timestamp => isValidDate(date),
        );
        const validPlannedOccurrences = (
          schedule.plannedOccurrences ?? []
        ).filter((occurrence): occurrence is PlannedOccurrence =>
          isValidDate(occurrence.date),
        );

        // O(1) day lookup instead of O(n×m) isSameDay scans over the entries array.
        const existingEntryDays = new Set(
          validEntries.map((entry) => startOfDay(entry.date).getTime()),
        );

        const originalRrule = schedule.rrule?.format;
        const { rrule: coercedRrule } = originalRrule
          ? coerceScheduleRrule(originalRrule)
          : { rrule: originalRrule };
        if (
          originalRrule &&
          !isValidRrule(originalRrule, { hasCount: !!schedule.frequency }) &&
          !activeReportedInvalidRrules.has(originalRrule)
        ) {
          activeReportedInvalidRrules.add(originalRrule);
          activeOnError?.(new Error('Invalid schedule rrule on read'), {
            taskId: snapshot.id,
            rrule: originalRrule,
          });
        }

        return {
          ...schedule,
          rrule: schedule.rrule
            ? { ...schedule.rrule, format: coercedRrule as string }
            : schedule.rrule,
          startDate: isValidDate(schedule.startDate)
            ? schedule.startDate
            : taskCreatedAt,
          endDate: firebaseTimestampToDate(schedule.endDate),
          entries: (
            [
              ...validEntries,
              ...validCompletions.map((date) =>
                existingEntryDays.has(startOfDay(date).getTime())
                  ? undefined
                  : {
                      id: `${date}`,
                      date: date,
                      state: ScheduleEntryState.Complete,
                      value: 1,
                    },
              ),
              ...validSkips.map((date) =>
                existingEntryDays.has(startOfDay(date).getTime())
                  ? undefined
                  : {
                      id: `${date}`,
                      date: date,
                      state: ScheduleEntryState.Skip,
                      value: 1,
                    },
              ),
            ] as ScheduleEntry[]
          )
            .filter(Boolean)
            .sort(
              (entryA, entryB) => entryA.date.getTime() - entryB.date.getTime(),
            ),
          completions: validCompletions.length ? validCompletions : null,
          skips: validSkips.length ? validSkips : null,
          plannedOccurrences: uniqBy(
            validPlannedOccurrences,
            // Deduplicate by *local* calendar day. Using the UTC ms truncation
            // (`getTime() / 86_400_000`) was wrong: rrule expansions write
            // occurrences at local `endOfDay()`, which in negative-UTC
            // timezones rolls into the next UTC day and collides with a
            // mid-day occurrence on the following local day.
            ({ date }) =>
              date.getFullYear() * 10_000 +
              date.getMonth() * 100 +
              date.getDate(),
          ),
          completionTarget: {
            count:
              typeof schedule.completionTarget?.count === 'number'
                ? schedule.completionTarget.count
                : 1,
            unit: coerceCompletionTargetUnit(schedule.completionTarget?.unit),
          },
        };
      });
    }

    return {
      ...data,
      ...parsedData,
    } as Task;
  },
  toFirestore: (task) => task,
};

export const createTaskConverter = ({
  onError,
}: TaskConverterOptions = {}): FirestoreDataConverter<Task> => {
  const reportedInvalidRrules = new Set<string>();

  return {
    fromFirestore: (snapshot) => {
      activeOnError = onError;
      activeReportedInvalidRrules = reportedInvalidRrules;
      return baseConverter.fromFirestore(snapshot);
    },
    toFirestore: (task) => task,
  };
};

export const taskConverter = createTaskConverter();

const excludedGoalIds = [goalInboxID, goalOtherID];

export const newTaskConverter = (task: NewTask) => {
  const deadline = task.endStrategy?.deadline;
  const isScheduledType = !!task.schedules?.length;

  return {
    ...task,
    type: resolveTaskTypeForSchedules(task.type, task.schedules),
    // habits/repeating tasks must never carry a deadlineDeadline; the
    // schedule defines due-ness.
    deadlineDeadline: isScheduledType
      ? null
      : (task as { deadlineDeadline?: Timestamp | null }).deadlineDeadline ??
        null,
    iconName: (task as NewHabit).iconName ?? null,
    createdAt: new Date(),
    startDate: task.startDate ? new Date(task.startDate) : new Date(),
    duration: (task as { duration?: number | null }).duration ?? null,
    frozenAt: null,
    completedAt: null,
    archivedAt: null,
    goalId: excludedGoalIds.includes(task.goalId!) ? null : task.goalId ?? null,
    lifeAreaId:
      task.lifeAreaId === lifeAreaOtherID ? null : task.lifeAreaId ?? null,
    reminders: task.reminders ?? null,
    // Any `reminders` value (incl. `[]`) is authoritative, so the legacy value can't resurface.
    reminderTime: task.reminders != null ? null : task.reminderTime ?? null,
    priority: task.priority ?? null,
    endStrategy: {
      deadline: deadline ? endOfDay(deadline) : null,
      completionCount: task.endStrategy?.completionCount ?? null,
    },
    schedules: task.schedules?.length
      ? task.schedules.map((schedule) => ({
          ...schedule,
          startDate: schedule.startDate
            ? new Date(schedule.startDate)
            : new Date(),
          endDate: schedule.endDate ? new Date(schedule.endDate) : null,
          entries: schedule.entries?.map((entry) => ({
            ...entry,
            date: entry.date ? new Date(entry.date) : null,
          })),
        }))
      : null,
  };
};

// Helper function to replace lodash/uniqBy
const uniqBy = <T>(array: T[], iteratee: (item: T) => unknown): T[] => {
  const seen = new Set();
  return array.filter((item) => {
    const key = iteratee(item);
    if (seen.has(key)) {
      return false;
    }
    seen.add(key);
    return true;
  });
};
